import { useCallback, useRef } from 'react';
import { getExcelFileName } from './ExcelUtil';
import { FlexGrid, CellType } from '@grapecity/wijmo.grid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import he from 'he';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import dayjs from 'dayjs';

type PdfDateSuffixType = 'date' | 'datetime' | 'none';
type PdfOrientation = 'p' | 'portrait' | 'l' | 'landscape';

type DownloadElementAsExcelOptions = {
  fileName?: string;
  sheetName?: string;
  exportClassName?: string;
  ignoreClassName?: string;
  scale?: number;
  imageMarginCol?: number;
  imageMarginRow?: number;
};

type DownloadElementAsPdfOptions = {
  fileName?: string;
  dateSuffixType?: PdfDateSuffixType;
  date?: Date;
  orientation?: PdfOrientation;
  format?: 'a4' | 'letter' | [number, number];
  scale?: number;
  backgroundColor?: string;
  marginMm?: number;

  /**
   * PDF 캡처에서 제외할 className
   * 예: 'pdf-ignore'
   */
  ignoreClassName?: string;

  /**
   * PDF 생성 직전에 element에 임시로 붙일 className
   * 예: 'pdf-export-mode'
   */
  exportClassName?: string;
};

export const CommonUtil = {
  downloadElementAsExcel: async (
    element: HTMLElement | null,
    options: DownloadElementAsExcelOptions = {}
  ) => {
    if (!element) return;

    const {
      fileName = '화면다운로드.xlsx',
      sheetName = '화면',
      exportClassName = 'excel-export-mode',
      ignoreClassName = 'excel-ignore',
      scale = 2,
      imageMarginCol = 1,
      imageMarginRow = 1,
    } = options;

    const waitNextFrame = () =>
      new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

    /**
     * Excel Sheet 이름 제약 처리
     * - 최대 31자
     * - 사용 불가 문자: \ / ? * [ ] :
     */
    const safeSheetName = sheetName.replace(/[\\/?*[\]:]/g, '_').substring(0, 31) || 'Sheet1';

    const safeFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;

    try {
      document.body.classList.add(exportClassName);

      await waitNextFrame();

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        ignoreElements: (el) => {
          if (!(el instanceof HTMLElement)) return false;

          return el.classList.contains(ignoreClassName);
        },
        onclone: (clonedDoc) => {
          clonedDoc.body.classList.add(exportClassName);

          const ignoredElements = clonedDoc.querySelectorAll(`.${ignoreClassName}`);

          ignoredElements.forEach((el) => {
            if (el instanceof HTMLElement) {
              el.style.display = 'none';
            }
          });
        },
      });

      const imageData = canvas.toDataURL('image/png');

      const workbook = new ExcelJS.Workbook();

      workbook.creator = 'React';
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet(safeSheetName, {
        pageSetup: {
          paperSize: 9, // A4
          orientation: 'portrait',
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
        },
      });

      const imageId = workbook.addImage({
        base64: imageData,
        extension: 'png',
      });

      /**
       * 실제 화면 기준 크기로 되돌림
       */
      const imageWidthPx = canvas.width / scale;
      const imageHeightPx = canvas.height / scale;

      worksheet.addImage(imageId, {
        tl: {
          col: imageMarginCol,
          row: imageMarginRow,
        },
        ext: {
          width: imageWidthPx,
          height: imageHeightPx,
        },
      });

      /**
       * 이미지 주변 Excel 셀 공간 확보
       */
      const estimatedRowCount = Math.ceil(imageHeightPx / 20) + imageMarginRow + 2;

      for (let rowIndex = 1; rowIndex <= estimatedRowCount; rowIndex += 1) {
        worksheet.getRow(rowIndex).height = 20;
      }

      const estimatedColCount = Math.ceil(imageWidthPx / 64) + imageMarginCol + 2;

      for (let colIndex = 1; colIndex <= estimatedColCount; colIndex += 1) {
        worksheet.getColumn(colIndex).width = 9;
      }

      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, safeFileName);
    } finally {
      document.body.classList.remove(exportClassName);
    }
  },
  /**
   * 파일명으로 쓰기 위험한 문자 제거
   */
  sanitizeFileName(fileName: string): string {
    return fileName
      .trim()
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, ' ');
  },
  /**
   * PDF 파일명 생성
   */
  buildPdfFileName(
    fileName = 'report',
    dateSuffixType: PdfDateSuffixType = 'date',
    date: Date = new Date()
  ): string {
    const safeFileName = this.sanitizeFileName(fileName) || 'report';

    if (dateSuffixType === 'none') {
      return `${safeFileName}.pdf`;
    }

    const suffix = dateSuffixType === 'datetime' ? this.toYmdHms(date) : this.toYmd(date);

    return `${safeFileName}_${suffix}.pdf`;
  },
  /**
   * 특정 HTML 영역을 PDF로 다운로드
   */
  async downloadElementAsPdf(
    element: HTMLElement | null,
    options: DownloadElementAsPdfOptions = {}
  ): Promise<void> {
    if (!element) return;

    const {
      fileName = 'report',
      dateSuffixType = 'date',
      date = new Date(),
      orientation = 'p',
      format = 'a4',
      scale = 2,
      backgroundColor = '#ffffff',
      marginMm = 0,
      ignoreClassName = 'pdf-ignore',
      exportClassName = 'pdf-export-mode',
    } = options;

    try {
      element.classList.add(exportClassName);

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        backgroundColor,
        ignoreElements: (targetElement) => {
          if (targetElement.classList?.contains('info')) {
            return true;
          }
          if (targetElement.classList?.contains(ignoreClassName)) {
            return true;
          }
          return false;
        },
        onclone: (clonedDoc) => {
          const clonedTextareas = clonedDoc.querySelectorAll('textarea');
          clonedTextareas.forEach((ta) => {
            const textarea = ta as HTMLTextAreaElement;

            // textarea의 부모 요소 찾기
            const parent = textarea.parentElement;
            if (!parent) return;

            // textarea의 스타일 복사
            const computedStyle = window.getComputedStyle(textarea);

            // 새로운 div 생성
            const div = clonedDoc.createElement('div');
            div.textContent = textarea.value;
            div.style.cssText = `
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            overflow: visible;
            width: ${computedStyle.width};
            height: auto;
            min-height: ${computedStyle.minHeight};
            max-height: ${computedStyle.maxHeight};
            padding: ${computedStyle.padding};
            margin: ${computedStyle.margin};
            border: ${computedStyle.border};
            border-radius: ${computedStyle.borderRadius};
            font-family: ${computedStyle.fontFamily};
            font-size: ${computedStyle.fontSize};
            line-height: ${computedStyle.lineHeight};
            color: ${computedStyle.color};
            background-color: ${computedStyle.backgroundColor};
            box-sizing: ${computedStyle.boxSizing};
            resize: none;
          `;

            // textarea를 div로 교체
            parent.replaceChild(div, textarea);
          });
        },
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const contentWidth = pageWidth - marginMm * 2;
      const contentHeight = pageHeight - marginMm * 2;

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = marginMm;

      pdf.addImage(imgData, 'PNG', marginMm, position, imgWidth, imgHeight);
      heightLeft -= contentHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + marginMm;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginMm, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      const downloadFileName = this.buildPdfFileName(fileName, dateSuffixType, date);
      pdf.save(downloadFileName);
    } finally {
      element.classList.remove(exportClassName);
    }
  },

  /**
   * 날짜 변환 (Date, string(2025.02.04) -> 20250204)
   * @param data
   * @Author jd
   */
  toYmd(v: Date | string): string | null {
    const tzOffset = 9 * 60 * 60 * 1000;
    const fmtDate = (d: Date) => {
      const local = new Date(d.getTime() + tzOffset);
      const y = local.getUTCFullYear();
      const m = String(local.getUTCMonth() + 1).padStart(2, '0');
      const day = String(local.getUTCDate()).padStart(2, '0');
      return `${y}${m}${day}`;
    };
    if (v instanceof Date && !isNaN(v.getTime())) return fmtDate(v);
    if (typeof v === 'string') {
      const s = v.trim().replace(/[-.]/g, '');
      if (/^\d{8}$/.test(s)) return s;
      const d = new Date(v);
      if (!isNaN(d.getTime())) return fmtDate(d);
    }
    return null;
  },
  /**
   * 예: 2026-05-19_143025
   * @param data
   * @Author jd
   */
  toYmdHms(date: Date = new Date()): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}_${hh}${mi}${ss}`;
  },
  /**
   * object -> array 변환
   * @param data
   * @Author jd
   */
  nomalizeToArray(data: any) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') return [data];
    return [];
  },
  /**
   * 소수점 {digits}자리까지 표현 (소수점 {digits + 1}자리에서 반올림처리)
   * @param value
   * @Author jd
   */
  roundNumber(value: number, digits = 1) {
    digits = Number(digits || 1);
    if (isNaN(digits)) {
      digits = 1;
    }
    return Number((Math.round(value * 100) / 100).toFixed(digits)).toLocaleString();
  },

  /**
   * 콤마(, ) 제거
   * @param str
   * @Author jd
   */
  removeComma(str = '') {
    return String(str || '').replace(/,/g, '');
  },
  /**
   * 숫자 타입으로 변환
   * ex. '1,234.0' -> 1234
   * @param str
   * @Author jd
   */
  convertNumber(str = '') {
    return Number(this.removeComma(str));
  },

  /**
   * 소수점 2자리 버림 처리 (값이 없는 경우 0 노출)
   * @param val
   */
  defaultZeroNumberTrunc(val) {
    const value = this.convertNumber(val || 0);
    if (!value || isNaN(value)) return 0;
    return (Math.trunc(value * 100) / 100).toLocaleString();
  },

  /**
   * 숫자 셀 템플릿 (값이 없는 경우 0 노출)
   * @param val
   * @Author jd
   * @constructor
   */
  defaultZeroNumberCellTemplate(val, digits = 1) {
    const value = this.convertNumber(val || 0);
    if (!value || isNaN(value)) return 0;
    return this.roundNumber(value, digits);
  },

  /**
   * 날짜포맷 (ex. 2024.05.05 - 2024.05.05)
   * @param stDt, endDt
   * @Author jd
   * @constructor
   */
  formatPeriod(stDt?: string, endDt?: string) {
    const parse = (dateStr?: string) => {
      if (!dateStr) return null;
      const d = dayjs(dateStr, ['YYYY-MM-DD', 'YYYY.MM.DD', 'YYYYMMDD'], true);
      return d.isValid() ? d : null;
    };

    const s = parse(stDt);
    const e = parse(endDt);

    if (!s && !e) return '';

    const fmt = (d: dayjs.Dayjs) => d.format('YYYY.MM.DD');

    if (s && e) return `${fmt(s)} - ${fmt(e)}`;
    if (s) return fmt(s);
    if (e) return fmt(e);

    return '';
  },
  /**
   * useRefValue
   * @param initialValue
   * @Author jd
   * @constructor
   */
  useRefValue<T>(initialValue: T) {
    const ref = useRef<T>(initialValue);

    /** 전체 객체 반환 */
    const get = useCallback(() => ref.current, []);

    /** 전체 객체 교체 */
    const set = useCallback((value: T) => {
      ref.current = value;
    }, []);

    /** 특정 key의 값 가져오기 */
    const getKeySafe = useCallback(
      <K extends keyof T>(key: K, defaultVal: NonNullable<T[K]>): NonNullable<T[K]> => {
        const cur = ref.current as T | undefined | null;
        if (cur == null) return defaultVal;
        const v = cur[key];
        return (v ?? defaultVal) as NonNullable<T[K]>;
      },
      []
    );

    /** 특정 key의 값 갱신 */
    const setKey = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
      ref.current = { ...ref.current, [key]: value };
    }, []);

    /** 여러 key를 한 번에 갱신 (Patch) */
    const patch = useCallback((partial: Partial<T>) => {
      ref.current = { ...ref.current, ...partial };
    }, []);

    /** 초기화 (원하면 초기값으로 복원 가능) */
    const reset = useCallback(() => {
      ref.current = structuredClone(initialValue);
    }, [initialValue]);

    return { get, set, getKeySafe, setKey, patch, reset };
  },
  /**
   * [ELM 공통 - Wijmo 엑셀 export]
   *
   * 엑셀 다운르드를 처리하는 함수
   * @Author jd
   **/
  exportWijmoExcelGrid(flexRef, fileName, sheetName) {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(
      flexRef,
      {
        sheetName: sheetName,
        includeColumnHeaders: true,
        includeRowHeaders: true,
        includeColumns: (col) => col.visible, // [26.09.17] 숨긴항목 엑셀에서 아예 제외
        formatItem: (args) => {
          const xlsxCell = args.xlsxCell;
          const cell = args.getFormattedCell();
          const text = cell?.textContent?.trim();
          xlsxCell.value = text;
          if (!text) {
            // [25.12.12] textContent 없는 경우 dataItem값 export
            const grid = args?.panel?.grid;
            if (!grid) return;
            if (args.panel === grid?.cells) {
              const col = grid?.columns?.[args.col];
              const item = grid?.rows?.[args.row]?.dataItem;
              if (item && col?.binding) {
                xlsxCell.value = item[col.binding] || '';
              }
            }
          }
        },
      },
      getExcelFileName(fileName),
      (base64) => {
        return;
      },
      (reason) => {
        console.error('error :', reason);
      },
      undefined,
      true,
      50
    );
  },

  /**
   * 시트명 오류 방지
   * @param name
   */
  cleanSheetName(name: string) {
    if (!name) return 'Sheet';
    // 시트명에 '/' 포함 시 오류 -> '_' 대체
    let result = name.replaceAll('/', '_');
    result = result.trim();
    if (result.length > 30) {
      result = result.substring(0, 30);
    }
    if (result.length < 1) result = 'Sheet';
    return result;
  },

  /**
   * 위즈모 엑셀 다운로드 시 시트 생성 (시트 다건 생성해야하는 경우 사용)
   * @param sheetNm
   * @param layoutDefinition
   * @param rowData
   * @param type // Modal의 경우 'MODAL' grid.dispose() 사용하지 않음
   */
  createWijmoSheet(sheetNm: string, layoutDefinition: any[], rowData: any[], type?: string) {
    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);

    const grid = new FlexGrid(container, {
      autoGenerateColumns: false,
      allowMerging: 'ColumnHeaders',
      columnGroups: layoutDefinition,
      itemsSource: rowData,
    });

    grid.columns.forEach((col) => {
      const len = (col.header || '').length;
      col.width = Math.max(100, len * 12); // 컬럼 넓이 수정
    });
    const result = wjGridXlsx.FlexGridXlsxConverter.save(grid, {
      includeColumnHeaders: true,
      includeStyles: true,
      includeColumns: (col) => col.visible, // [26.09.17] 숨긴항목 엑셀에서 아예 제외
      formatItem: (args) => {
        if (args.panel === grid!.cells) {
          const cell = args.getFormattedCell();
          if (cell) {
            args.xlsxCell.value = cell?.textContent?.trim();
          }
        }
        if (args.panel.cellType === CellType.Cell) {
          args.xlsxCell.style = args.xlsxCell.style || {};
          args.xlsxCell.style.fill = {
            color: '#FFFFFF', // 배경색 흰색으로 변경
          };
        }
      },
    });

    if (type !== 'MODAL') {
      grid.dispose();
    }
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }

    const sheet = result.sheets[0];
    sheet.name = this.cleanSheetName(sheetNm);
    return sheet;
  },

  /**
   * [ELM 공통 - HTML 엔티티 치환 함수]
   *
   * Html String을 unescapeHtml 처리하는 함수.
   * @Author jd
   **/
  unescapeHtml(htmlString: string) {
    if (!htmlString) return '';

    /*
    const matchMap: { [key: string]: string } = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&#40;': '(',
      '&#41;': ')',
      '&#35;': '#',
      '&#59;': ':',
      '&#34;': '"',
      '&#39;': "'",
      '&#92;': '\\',
      '&#47;': '/',
      '&Phi;': 'Φ',
      '&plusmn;': '±',
      '&quot;': '"',
    };

    let result = htmlString;

    while (prev !== result) {
      prev = result;
      result = result.replace(
        /&amp;|&lt;|&gt;|&#40;|&#41;|&#35;|&#59;|&#34;|&#39;|&#92;|&#47;|&Phi;|&plusmn;/g,
        (match) => matchMap[match] || match
      );
    }
    */

    let prev = '';
    let result = htmlString;
    while (prev != result) {
      prev = result;
      result = he.decode(result);
    }
    return result;
  },

  /**
   * [ELM 공통 - HTML 엔티티 치환 함수]
   *
   * Html String을 escapeHtml 처리하는 함수.
   * @Author jd
   **/
  escapeHtml(htmlString) {
    return htmlString?.replace(/[&<>()#:"'\\/]/g, (match) => {
      switch (match) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '(':
          return '&#40;';
        case ')':
          return '&#41;';
        case '#':
          return '&#35;';
        case ':':
          return '&#59;';
        case '"':
          return '&#34;';
        case "'":
          return '&#39;';
        case '\\':
          return '&#92;';
        case '/':
          return '&#47;';
        default:
          return match;
      }
    });
  },

  /**
   * [ELM 공통 - XSS 공격 방지용 함수]
   *
   * 공지사항 같은 경우 HTML을 그대로 노출시켜야하므로
   * 해당 함수를 사용하여 sanitize 처리 하도록 한다.
   * 그리드에서 사용할 경우 escape, 아닐 경우 unescape처리
   *
   * @Author jd
   **/
  safeHtmlContents(html, isGrid = true) {
    if (!html || typeof html !== 'string') return html;
    let decodeStr = this.unescapeHtml(html) || '';
    decodeStr = decodeStr
      .replace(/<script.*?>.*?<\/script>/g, '')
      .replace(/<style.*?>.*?<\/style>/g, '')
      .replace(/<iframe.*?>.*?<\/iframe>/g, '')
      .replace(/<details.*?>.*?<\/details>/g, '')
      .replace(/<img.*?>.*?<\/img>/g, '')
      .replace(/<object.*?>.*?<\/object>/g, '')
      .replace(/<[^>]*?\s+on\w+=(["']?)[^"'>]*\1[^>]*?>/gi, '');
    decodeStr = decodeStr?.replace(
      /<[^>]*\s(?:on\w+=|javascript:|<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>).*?>/gi,
      ''
    );

    if (isGrid) {
      return this.escapeHtml(decodeStr);
    }

    return this.unescapeHtml(decodeStr);
  },

  /**
   * HTML 엔티티를 일반 문자열로 디코드합니다.
   * ex) &lt;div&gt; → <div>
   */
  decodeContents(contents) {
    let ta: HTMLTextAreaElement | null = null;
    if (!contents) return '';
    if (!ta) ta = document.createElement('textarea');
    ta.innerHTML = contents;
    return ta.value;
  },

  /**
   * @param {string} value - 사용자가 입력한 값
   * @param {number} decimalPlaces - 0이면 정수, 1 이상이면 해당 자릿수까지 소수점 허용
   * @returns {string} 정규식 필터링이 완료된 문자열
   */
  onlyNumInput(value, decimalPlaces = 0) {
    // 1. 파라미터가 0일 경우: 정수만 허용 (소수점 입력 자체를 차단)
    if (decimalPlaces === 0) {
      const match = value.match(/^\d*/);
      return match ? match[0] : '';
    }

    // 2. 파라미터가 1 이상일 경우: 지정된 소수점 자릿수까지 허용
    if (decimalPlaces >= 1) {
      const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?`);
      const match = value.match(regex);
      return match ? match[0] : '';
    }

    // 음수 등 잘못된 파라미터가 들어올 경우를 대비한 기본값 처리
    return '';
  },
};
